/* Product guide tabs. Referenced Page content is rendered by Liquid. */
(() => {
  'use strict';

  const tagName = 'ms-installation-tabs';
  if (!window.customElements || window.customElements.get(tagName)) return;

  class MSInstallationTabs extends HTMLElement {
    constructor() {
      super();
      this._initialized = false;
      this._onReady = () => this._initialize();
      this._onHashChange = () => this._revealHash(window.location.hash);
      this._onClick = (event) => this._handleClick(event);
      this._onKeyDown = (event) => this._handleKeyDown(event);
    }

    connectedCallback() {
      // A deferred asset normally upgrades complete markup. These two local
      // entry points also cover parser-created and dynamically inserted hosts.
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', this._onReady, { once: true });
      }
      queueMicrotask(this._onReady);
    }

    disconnectedCallback() {
      document.removeEventListener('DOMContentLoaded', this._onReady);
      window.removeEventListener('hashchange', this._onHashChange);
      this.removeEventListener('click', this._onClick);
      this.removeEventListener('keydown', this._onKeyDown);
      this._initialized = false;
    }

    _owned(selector) {
      return Array.from(this.querySelectorAll(selector)).filter(
        (node) => node.closest(tagName) === this
      );
    }

    _initialize() {
      if (!this.isConnected || this._initialized) return;
      const list = this._owned('[data-guide-tablist]')[0];
      const tabs = this._owned('[data-guide-tab]');
      const panels = this._owned('[data-guide-panel]');
      if (!list || tabs.length < 2 || tabs.length !== panels.length) return;

      const linkedPanels = tabs.map((tab) => panels.find(
        (panel) => panel.id === tab.getAttribute('aria-controls')
      ));
      if (linkedPanels.some((panel) => !panel) || new Set(linkedPanels).size !== tabs.length) return;

      this._tabs = tabs;
      this._panels = linkedPanels;
      this._initialized = true;
      this.addEventListener('click', this._onClick);
      this.addEventListener('keydown', this._onKeyDown);
      window.addEventListener('hashchange', this._onHashChange);
      const selected = tabs.findIndex((tab) => tab.getAttribute('aria-selected') === 'true');
      this._activate(selected < 0 ? 0 : selected);
      list.hidden = false;
      this._revealHash(window.location.hash);
    }

    _activate(index, focus = false) {
      if (index < 0 || index >= this._tabs.length) return;
      this._tabs.forEach((tab, position) => {
        const selected = position === index;
        tab.setAttribute('aria-selected', String(selected));
        tab.tabIndex = selected ? 0 : -1;
        this._panels[position].hidden = !selected;
      });
      if (focus) this._tabs[index].focus({ preventScroll: true });
    }

    _handleClick(event) {
      if (event.defaultPrevented || event.button !== 0 || event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return;
      if (!(event.target instanceof Element)) return;
      const tab = event.target.closest('[data-guide-tab]');
      const index = this._tabs.indexOf(tab);
      if (index !== -1) {
        this._activate(index);
        return;
      }

      // Reveal an in-page anchor before the browser's ordinary navigation.
      // New-tab clicks, other documents and canonical Page links stay native.
      const link = event.target.closest('a[href]');
      if (!link || link.closest(tagName) !== this || link.hasAttribute('download') ||
          (link.target && link.target !== '_self')) return;
      let url;
      try { url = new URL(link.href, window.location.href); } catch (_) { return; }
      if (url.origin === window.location.origin && url.pathname === window.location.pathname &&
          url.search === window.location.search && url.hash) {
        this._revealHash(url.hash);
      }
    }

    _handleKeyDown(event) {
      if (event.defaultPrevented || event.altKey || event.ctrlKey || event.metaKey) return;
      if (!(event.target instanceof Element)) return;
      const index = this._tabs.indexOf(event.target.closest('[data-guide-tab]'));
      if (index === -1) return;
      let next;
      if (event.key === 'ArrowRight') next = (index + 1) % this._tabs.length;
      else if (event.key === 'ArrowLeft') next = (index + this._tabs.length - 1) % this._tabs.length;
      else if (event.key === 'Home') next = 0;
      else if (event.key === 'End') next = this._tabs.length - 1;
      else return;
      event.preventDefault();
      this._activate(next, true);
    }

    _revealHash(hash) {
      if (!this._initialized || !hash || hash === '#') return;
      let id;
      try { id = decodeURIComponent(hash.slice(1)); } catch (_) { return; }
      const target = this._owned('[id]').find((node) => node.id === id);
      if (!target) return;
      const tabIndex = this._tabs.indexOf(target);
      const index = tabIndex !== -1 ? tabIndex : this._panels.findIndex(
        (panel) => panel === target || panel.contains(target)
      );
      if (index === -1) return;
      this._activate(index);
      let details = target.closest('details');
      while (details) {
        details.open = true;
        details = details.parentElement ? details.parentElement.closest('details') : null;
      }
      // No forced focus, scroll, history mutation or calculator interaction.
    }
  }

  window.customElements.define(tagName, MSInstallationTabs);
})();
